import {
  type Logger,
  getLogger,
  postAppBridgeMessage,
  registerNotificationHandler,
  unregisterNotificationHandler,
  clearJsCallTimer,
  getJsCallId,
} from '../../bridge';
import {
  Bridge,
  NativeAPIResponseMessage,
  BridgeOptions,
} from '@zoom/hybrid-jsbridge';
import { genRandomId } from '../utils';
import { isPending } from '../../utils/promise';
import apps from '../apps';

// Enums
export enum RESULT_CODE {
  SUCCESS = 0,
  CALL_SDK_TIME_OUT = -100,
}

// Constants
const CONFIG_TIMEOUT_MS = 100;
const DEFAULT_REQUEST_TIMEOUT_MS = 30 * 1000;

// Pre-built error objects for performance
const TIMEOUT_ERROR_OBJECT = {
  returnCode: RESULT_CODE.CALL_SDK_TIME_OUT,
  errorCode: RESULT_CODE.CALL_SDK_TIME_OUT,
};
const SUCCESS_RESPONSE_OBJECT = {
  returnCode: RESULT_CODE.SUCCESS,
  errorCode: RESULT_CODE.SUCCESS,
};

const timeoutErrorStr = JSON.stringify(TIMEOUT_ERROR_OBJECT);

// Enhanced type definitions
interface AppBridgeEventData {
  jsCallId?: string;
  result?: {
    reqId?: string;
    req_id?: string;
    [key: string]: any;
  };
  reqId?: string;
  req_id?: string;
  [key: string]: any;
}

export type TimerInfo = {
  service: string;
  action: string;
  jsCallId: string;
  timeout: number;
  beginTime: number;
};

// Module-level variables
let appBridgeInited = false;
let appBridgeReady = false;
const registeredEvents: Set<string> = new Set();
const ignoreEvents: Set<string> = new Set();
const pendingRequest: Map<
  RequestId,
  { resolve: (p: any) => void; reject: (e: Error) => void }
> = new Map();
let appEventHandler: (e: EventNotify) => void;
const pendingTimer: Map<RequestId, any> = new Map();
const pendingTimerInfo: Map<RequestId, TimerInfo> = new Map();

// Module-level service cache to avoid recreation
const serviceCache: { [key: string]: Service } = {};

let logger: Logger;

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.config
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
async function config(cfg: Config): Promise<AppBridgeConfigResult> {
  // Skip if appBridge is already initialized
  if (appBridgeInited) {
    logger.warn(
      'hybrid.appBridge skip config, appBridge already configured, please call reset() then re-config!'
    );
    return {
      returnCode: 0,
      result: 'Success',
      message:
        'hybrid.appBridge skip config, appBridge already configured, please call reset() then re-config!',
    };
  }

  appBridgeInited = true;

  // Initialize bridge instance if config exists
  if (cfg.js_bridge_config) {
    Bridge.getInstance(cfg.js_bridge_config);
  }

  // Configure logging if enabled
  if (cfg.log) {
    configLog(cfg);
  }

  // if config not supported, we need to manual remove timer of config API.
  const configJsCallId = genRandomId();
  const js_api_lists = Array.from(
    new Set([
      'zmAppBridgePub',
      'zmAppBridgeSub',
      'zmOnAppBridgeEvent',
      ...(cfg.js_api_lists || []),
    ])
  );
  logger = getLogger();

  let autoConfigAppsResult: AppBridgeConfigResponse | null = null;
  if (cfg.auto_config_apps) {
    // Wait for auto config apps to complete
    try {
      const result = await handleAutoConfigApps(js_api_lists);
      autoConfigAppsResult = result?.result;
    } catch (e) {
      logger.warn('hybrid.appBridge auto_config_apps failed:', e);
    }
  }

  // Start configJsAPIList with timeout handling in parallel
  const configRequest = configJsAPIList({ js_api_lists }, configJsCallId);
  const jsAPIListPromise = handleConfigTimeout(configRequest, configJsCallId);

  // Configure event handling
  eventConfig(cfg);
  registerNotificationHandler(appBridgeEventHandler);

  try {
    if (cfg.subscribe?.length) {
      // Execute configSubscribe first to not block it
      const subscribeRes = await configSubscribe(cfg)!;

      // Wait for jsAPIList to complete or timeout
      // This won't block the response since we already have subscribeRes
      await jsAPIListPromise;

      if (subscribeRes.returnCode === 0) {
        logger.info('hybrid.appBridge config successfully! 🚀');
        appBridgeReady = true;
        return {
          ...subscribeRes,
          ...(autoConfigAppsResult && { configuration: autoConfigAppsResult }),
        };
      } else {
        throw new Error(
          `hybrid.appBridge configSubscribe failed, please check your subscribe config, returnCode: ${subscribeRes.returnCode}`
        );
      }
    } else {
      appBridgeReady = true;
      return {
        returnCode: 0,
        result: 'Success',
        message:
          'hybrid.appBridge config app bridge finished. subscribe is empty, skipping call to zmAppBridgeSub.',
        ...(autoConfigAppsResult && { configuration: autoConfigAppsResult }),
      };
    }
  } catch (e) {
    // Reset state on error
    reset();
    throw e;
  }
}

async function handleAutoConfigApps(js_api_lists: string[]) {
  logger.info(
    'hybrid.appBridge auto_config_apps is enabled, calling apps config...'
  );

  try {
    const res = await apps.config({ capabilities: js_api_lists });
    logger.info(
      'hybrid.appBridge auto_config_apps is enabled, apps.config response with',
      res
    );
    return res;
  } catch (e) {
    logger.warn(
      'hybrid.appBridge auto_config_apps is enabled, but the app config does not respond!',
      e
    );
    throw e;
  }
}

async function handleConfigTimeout(
  configRequest: Promise<any>,
  configJsCallId: string
) {
  return Promise.race([
    configRequest,
    new Promise<void>((resolve) => setTimeout(resolve, CONFIG_TIMEOUT_MS)),
  ])
    .then(async () => {
      if (await isPending(configRequest)) {
        clearJsCallTimer(configJsCallId);
        logger.warn(
          `hybrid.appBridge skip config API, remove config API timer after ${CONFIG_TIMEOUT_MS}ms`
        );
      }
    })
    .catch((e) => {
      logger.error('hybrid.appBridge configJsAPIList error', e);
    });
}

function configSubscribe(subscribeConfig: AppBridgeSubscribeConfig) {
  if (subscribeConfig.subscribe && subscribeConfig.subscribe.length > 0) {
    subscribeConfig.subscribe.forEach(({ eventNames }) => {
      eventNames?.forEach((eventName) => {
        registeredEvents.add(eventName);
      });
    });

    return postAppBridgeMessage<
      { data: AppBridgeSubscribe[] },
      NativeAPIResponseMessage<string>
    >('zmAppBridgeSub', { data: subscribeConfig.subscribe });
  }
}

function configJsAPIList(apiConfig: AppBridgeAPIConfig, jsCallId: string) {
  return postAppBridgeMessage<
    AppBridgeAPIConfig,
    NativeAPIResponseMessage<AppBridgeConfigResponse>
  >('config', apiConfig, { jsCallId });
}

function eventConfig(cfg: IgnoreEventsConfig & OnAppEventNotifyConfig) {
  cfg.ignoreEvents?.forEach((ignoreEvent) => {
    ignoreEvents.add(ignoreEvent);
  });

  if (cfg.onAppEventNotified) {
    appEventHandler = cfg.onAppEventNotified;
  }
}

function configLog(logConfig: LogConfig) {
  Bridge.getInstance({
    debug: logConfig.log,
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.reset
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
function reset() {
  if (!appBridgeInited) return;

  // Enhanced cleanup: clear all pending timers first
  for (const [, timer] of pendingTimer) {
    if (timer) {
      clearTimeout(timer);
    }
  }

  // Reject all pending requests with proper error
  for (const [, { reject }] of pendingRequest) {
    reject(new Error('AppBridge reset - all pending requests cancelled'));
  }

  // Clear service cache
  Object.keys(serviceCache).forEach((key) => delete serviceCache[key]);

  appBridgeInited = false;
  appBridgeReady = false;
  unregisterNotificationHandler(appBridgeEventHandler);
  registeredEvents.clear();
  ignoreEvents.clear();
  pendingRequest.clear();
  appEventHandler = () => {};
  pendingTimer.clear();
  pendingTimerInfo.clear();
}

export type Service = {
  pub: <T, R>(pubMessage: PubMessage<T>) => Promise<R>;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.getService
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
function getService(config: { service: string }): Service | undefined {
  const { service } = config;

  validateServiceRequest(service);

  if (!serviceCache[service]) {
    serviceCache[service] = createServiceInstance(service);
  }

  return serviceCache[service];
}

function validateServiceRequest(service: string) {
  if (!appBridgeInited) {
    throw new Error(
      '[Hybrid.appBridge] App bridge is not configured, please call config() first!'
    );
  }
  if (!appBridgeReady) {
    throw new Error(
      '[Hybrid.appBridge] App bridge is not ready, please await config() finish!'
    );
  }
  if (!service) {
    throw new Error(
      `[Hybrid.appBridge] App bridge getService got a empty service [${service}]!`
    );
  }
}

function createServiceInstance(service: string): Service {
  return {
    pub: <T, R>(pubMessage: PubMessage<T>) => {
      return handleServicePub<T, R>(service, pubMessage);
    },
  };
}

function handleServicePub<T, R>(
  service: string,
  pubMessage: PubMessage<T>
): Promise<R> {
  const {
    action,
    params = {} as T,
    jsCallId: overrideJsCallId,
    timeout = DEFAULT_REQUEST_TIMEOUT_MS,
    needResponse = true,
  } = pubMessage;

  const jsCallId = overrideJsCallId || getJsCallId();

  // Pre-build message object for efficiency
  const messageObject = { action, params, jsCallId };
  const data = JSON.stringify(messageObject);

  const promise: Promise<R> = new Promise((resolve, reject) => {
    pendingRequest.set(jsCallId, { resolve, reject });
  });

  if (needResponse) {
    addTimerForRequest({ service, action, jsCallId, timeout });
  } else {
    resolvedDirectly(jsCallId);
  }

  postAppBridgeMessage(
    'zmAppBridgePub',
    { data, service },
    overrideJsCallId ? { jsCallId } : {}
  )
    .then((res) => {
      if (res && res.returnCode !== 0) {
        rejectRequest(jsCallId, res);
      }
    })
    .catch((e) => {
      const pendingReq = pendingRequest.get(jsCallId);
      if (pendingReq) {
        pendingReq.reject(e);
        pendingRequest.delete(jsCallId);
      }
    });

  return promise;
}

function addTimerForRequest(request: {
  service: string;
  action: string;
  jsCallId: string;
  timeout: number;
}) {
  const { service, action, jsCallId, timeout } = request;
  if (timeout > 0) {
    const timer = setTimeout(() => {
      logger.warn(
        `hybrid.appBridge Timeout: The native client did not provide a response to the App Bridge call, service:${service} action:${action}`
      );

      const pendingReq = pendingRequest.get(jsCallId);
      if (!pendingReq) {
        logger.error(
          `hybrid.appBridge missing pending request for ${jsCallId}`
        );
        return;
      }

      pendingReq.reject(
        new Error(
          `[hybrid/jsbridge]AppBridge timeout, service:${service} action:${action} ${timeoutErrorStr}`
        )
      );

      pendingRequest.delete(jsCallId);
      removeTimerById(jsCallId);
    }, timeout);

    pendingTimer.set(jsCallId, timer);
    pendingTimerInfo.set(jsCallId, {
      service,
      action,
      jsCallId,
      timeout,
      beginTime: Date.now(),
    });
  }
}

function resolvedDirectly(jsCallId: string) {
  const pendingReq = pendingRequest.get(jsCallId);
  if (pendingReq) {
    pendingReq.resolve(SUCCESS_RESPONSE_OBJECT);
    pendingRequest.delete(jsCallId);
  }
}

const appBridgeEventHandler = (
  eventData: NativeAPIResponseMessage<AppBridgeEventData>
) => {
  const { returnCode, result: eventResult } = eventData;
  if (returnCode === 0) {
    const { eventName } = eventResult;
    let eventData = eventResult.eventData;
    if (registeredEvents.has(eventName)) {
      if (typeof eventData === 'string') {
        eventData = safeJsonParse(eventData);
      }
      if (eventData) {
        const { jsCallId, result } = eventData;

        if (jsCallId && !ignoreEvents.has(eventName)) {
          /**
           * 1. If there is no reqId, it means this is the final response data.
           * 2. If the response data contains reqId, it means this is an asynchronous notification request,
           * and a true response notification with the same reqId will follow shortly.
           */
          const { reqId } = result || {};
          if (reqId) {
            // wait async response from notification with reqId
            jsCallIdUpgradeToReqId(jsCallId, reqId);
          } else {
            // directly response
            resolvedRequest(jsCallId, eventData);
          }
        } else {
          // notification with reqId was received, resolve the request with reqId
          const reqId = getReqId(eventData);
          if (reqId) {
            resolvedRequest(reqId, eventData);
          }
        }
      }
      appEventHandler?.({ eventName, eventData });
    }
  } else {
    const jsCallId = eventResult?.eventData?.jsCallId;
    const eventName = eventResult?.eventName;
    const eventData = eventResult?.eventData;
    rejectRequest(jsCallId, eventData);
    appEventHandler?.({ eventName, eventData });
  }
};

function resolvedRequest(id: string, data: NativeAPIResponseMessage<any>) {
  const pendingReq = pendingRequest.get(id);
  if (pendingReq) {
    pendingReq.resolve(data);
    pendingRequest.delete(id);
    removeTimerById(id);
  }
}

function rejectRequest(id: string, response: NativeAPIResponseMessage<any>) {
  if (!id) {
    logger.warn(
      `hybrid.appBridge can't rejectRequest with empty jsCallId. PendingRequests: ${pendingRequest.size}`,
      response
    );
  } else {
    const pendingReq = pendingRequest.get(id);
    if (pendingReq) {
      pendingReq.reject(
        new ZoomAppBridgeError(
          `[hybrid/jssdk]AppBridge ErrorResponse ${JSON.stringify(response)}`,
          response
        )
      );
      pendingRequest.delete(id);
      removeTimerById(id);
    }
  }
}

function jsCallIdUpgradeToReqId(jsCallId: string, reqId: string) {
  const pendingReq = pendingRequest.get(jsCallId);
  if (pendingReq) {
    const timerInfo = pendingTimerInfo.get(jsCallId);

    pendingRequest.set(reqId, pendingReq);

    // Clean up old entries
    const timer = pendingTimer.get(jsCallId);
    if (timer) {
      clearTimeout(timer);
    }
    pendingTimer.delete(jsCallId);
    pendingTimerInfo.delete(jsCallId);
    pendingRequest.delete(jsCallId);

    if (timerInfo) {
      const updatedTimerInfo = {
        ...timerInfo,
        jsCallId: reqId,
        timeout: Math.max(
          1,
          timerInfo.timeout - (Date.now() - timerInfo.beginTime)
        ),
      };
      addTimerForRequest(updatedTimerInfo);
    } else {
      logger.error(
        `hybrid.appBridge jsCallIdUpgradeToReqId failed - missing timer info for ${jsCallId}`
      );
    }
  }
}

function getReqId(eventData: {
  reqId?: string;
  req_id?: string;
}): string | undefined {
  const { reqId, req_id } = eventData;
  return reqId || req_id;
}

function safeJsonParse(json: string) {
  try {
    return JSON.parse(json);
  } catch (error) {
    logger.warn(
      `[hybrid/appBridge] safeJsonParse Parse error, invalid json ${json} ${error}`
    );
  }
  return null;
}

function removeTimerById(id: string) {
  const timer = pendingTimer.get(id);
  if (timer) {
    clearTimeout(timer);
    pendingTimer.delete(id);
  }
  // Always clean up timer info regardless
  pendingTimerInfo.delete(id);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.pub
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
// to support hybrid.isAvailable('appBridge.pub')

export type PubMessage<T> = {
  action: string;
  params?: T;
  jsCallId?: string;
  timeout?: number; // milliseconds
  needResponse?: boolean;
};

export type RequestId = string;
export type Config = AppBridgeSubscribeConfig &
  IgnoreEventsConfig &
  OnAppEventNotifyConfig &
  AppBridgeAPIConfig &
  LogConfig &
  JSBridgeConfig;

export type JSBridgeConfig = {
  js_bridge_config?: Partial<BridgeOptions>;
};

export type AppBridgeAPIConfig = {
  js_api_lists?: string[];
  auto_config_apps?: boolean;
};
export type AppBridgeSubscribeConfig = {
  subscribe?: AppBridgeSubscribe[];
};
export type AppBridgeSubscribe = {
  service: string;
  eventNames: string[];
};

export type IgnoreEventsConfig = { ignoreEvents?: string[] };
export type LogConfig = {
  log?: {
    enable: boolean;
    output: {
      info: (...data: any[]) => void;
      warn: (...data: any[]) => void;
      error: (...data: any[]) => void;
    };
  };
};

export type OnAppEventNotifyConfig = {
  onAppEventNotified?: EventNotifyHandler;
};
export type EventNotify = { eventName: string; eventData?: any };
export type EventNotifyHandler = (e: EventNotify) => void;

export type AppBridgeConfigResponse = {
  auth: {
    status: string;
    upgradable: boolean;
  };
  browserVersion: string;
  clientVersion: string;
  launchContext: {
    openBy: string;
  };
  runningContext: string;
  themeMode: 'light' | 'dark' | string;
  unsupportedApis: string[];
  userAgent: string;
};

export type AppBridgeConfigResult = {
  returnCode: number;
  result: string | NativeAPIResponseMessage<string>;
  message?: string;
  configuration?: AppBridgeConfigResponse;
  jsCallId?: string;
  originalCode?: number;
  errorMsg?: string;
  name?: string;
  code?: number;
  msg_type?: string;
  context?: {
    page?: string;
  };
  [key: string]: any;
};

export type Pub<T> = {
  service: string;
  data: T;
};

export class ZoomAppBridgeError extends Error {
  public response: NativeAPIResponseMessage<any>;
  /** @hidden */
  constructor(message: string, response: NativeAPIResponseMessage<any>) {
    super(message);
    this.response = response;
    Object.setPrototypeOf(this, ZoomAppBridgeError.prototype);
  }
}

export default {
  config,
  reset,
  getService,
};
